import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { CartFunnelPageConfig } from '@front-platform/content-builder';
import { cartFunnelPageResolveConfig } from '@front-platform/content-builder';
import { resolveCartFunnelPadding } from '@shared/blocks/kiosk/cart-funnel/kiosk-cart-funnel-top-padding';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  input,
  OnDestroy,
  OnInit,
  untracked,
} from '@angular/core';
import {
  debounceTime,
  delay,
  distinctUntilChanged,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Observable, Subject } from 'rxjs';

import { Paths } from '@config/paths.config';
import { IOnBehalfUser } from '@core/models/order.model';
import { DoodCartFunnelValueTarget, isCartFunnelStepComplete } from '@store/cart-funnel';

import { BasketStoreSelector } from '@common/selectors/basket.selector';
import { CartFunnelStoreSelector } from '@common/selectors/cart-funnel.selector';
import { CartFunnelStoreDispatcher } from '@common/dispatchers/cart-funnel.dispatcher';
import { ContentStoreDispatcher } from '@common/dispatchers/content.dispatcher';

import { CartService } from '@core/services/cart/cart.service';
import { CartFunnelContinueService } from '@core/services/cart-funnel/cart-funnel-continue.service';
import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { NavigationHistoryService } from '@core/services/navigation-history/navigation-history.service';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { normalizeCartFunnelPageBody, planCartFunnelStepsSync } from './cart-funnel-page.util';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';

export interface CartFunnelPageRuntimeConfig {
  'body'?: unknown[];
  'bottom_content'?: unknown[];
  'content'?: unknown[];
  'header'?: unknown[];
  'background_color'?: IContentBuilderFieldColor;
}

export type CartFunnelPageConfigInput = Partial<
  Omit<CartFunnelPageConfig, keyof CartFunnelPageRuntimeConfig> & CartFunnelPageRuntimeConfig
>;
export type CartFunnelPageResolvedConfig = Omit<
  CartFunnelPageConfig,
  keyof CartFunnelPageRuntimeConfig
> &
  Partial<CartFunnelPageRuntimeConfig>;
export interface CartFunnelPageContract extends ConfigComponentContract<
  CartFunnelPageConfigInput,
  CartFunnelPageResolvedConfig
> {}

@Component({
  selector: 'app-cart-funnel-page',
  templateUrl: './cart-funnel-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CartFunnelPageComponent implements OnInit, OnDestroy, CartFunnelPageContract {
  readonly config = input<CartFunnelPageConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    CartFunnelPageConfig,
    CartFunnelPageRuntimeConfig
  >(cartFunnelPageResolveConfig, () => ({ config: this.config() }));
  private _destroyerRef = new Subject<boolean>();

  body = computed(() => this.resolvedConfig()['body'] as unknown as unknown[] | undefined);
  bottomContent = computed(
    () => this.resolvedConfig()['bottom_content'] as unknown as unknown[] | undefined,
  );
  bottomPadding = computed(() => resolveCartFunnelPadding(this.resolvedConfig().bottom_padding));
  content = computed(() => this.resolvedConfig()['content'] as unknown as unknown[] | undefined);
  header = computed(() => this.resolvedConfig()['header'] as unknown as unknown[]);
  backgroundColor = computed(
    () => this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor,
  );
  backgroundImage = computed(
    () =>
      this.resolvedConfig()['background_image'] as unknown as IContentBuilderFieldImage | undefined,
  );
  backgroundImageCss = computed(() => {
    const url = this.backgroundImage()?.url;
    return url ? `url(${url})` : null;
  });

  protected readonly steps = computed(() => {
    return normalizeCartFunnelPageBody(this.body());
  });

  isValid$: Observable<boolean> = this.cartFunnelSelector.selectActiveStep.pipe(
    switchMap(active => {
      return this.cartFunnelSelector.selectValues.pipe(
        map(values => values.filter(v => v.formId === active?.id)),
      );
    }),
    map(values => isCartFunnelStepComplete(values)),
  );

  isLastItem = true;
  isFormValid = false;

  /** Signature of steps last projected into the cart-funnel store. */
  private lastSyncedStepIds: string | null = null;
  private displayableSkipWatchArmed = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private readonly router: Router,
    private readonly cartService: CartService,
    private basketSelector: BasketStoreSelector,
    private readonly orderDispatcher: OrderStoreDispatcher,
    private readonly activatedRoute: ActivatedRoute,
    private readonly routerHelper: RouterHelperService,
    private cartFunnelSelector: CartFunnelStoreSelector,
    private cartFunnelDispatcher: CartFunnelStoreDispatcher,
    private readonly navigationHistoryService: NavigationHistoryService,
    private readonly analyticsService: AnalyticsService,
    private readonly settingsSelector: SettingsStoreSelector,
    private readonly contentDispatcher: ContentStoreDispatcher,
    private readonly cb2PreviewState: Cb2PreviewStateService,
    private readonly cartFunnelContinue: CartFunnelContinueService,
  ) {
    this.cartFunnelContinue.requests$
      .pipe(takeUntil(this._destroyerRef))
      .subscribe(() => this.submit());
    this.cartFunnelSelector.selectIsLastStepActive
      .pipe(takeUntil(this._destroyerRef))
      .subscribe(_isLastItem => {
        this.isLastItem = _isLastItem;
        this.cdr.markForCheck();
      });

    // Re-project body steps whenever soft-refresh updates the draft (empty boot → form).
    effect(() => {
      const steps = this.steps();
      untracked(() => this.syncFunnelStepsFromBody(steps.map(step => step.block)));
    });
  }

  ngOnInit(): void {
    this.cartFunnelSelector.selectActiveStep
      .pipe(
        switchMap(active => {
          return this.cartFunnelSelector.selectValues.pipe(
            map(values => values.filter(v => v.formId === active?.id)),
          );
        }),
        map(values => isCartFunnelStepComplete(values)),
        debounceTime(200),
        distinctUntilChanged(),
        takeUntil(this._destroyerRef),
      )
      .subscribe(_isValid => {
        this.isFormValid = _isValid;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.cartFunnelDispatcher.leaveCartFunnel();

    this._destroyerRef.next(true);
    this._destroyerRef.complete();
  }

  private syncFunnelStepsFromBody(stepIds: readonly string[]): void {
    const plan = planCartFunnelStepsSync({
      stepIds,
      previouslySyncedKey: this.lastSyncedStepIds,
      isSharedOrder: !!this.basketSelector.basket.id,
      isPreview: this.cb2PreviewState.isActive(),
    });

    this.lastSyncedStepIds = plan.nextSyncedKey;

    switch (plan.action) {
      case 'noop':
        return;
      case 'skip':
        this.skipFunnel();
        return;
      case 'clear':
        this.cartFunnelDispatcher.leaveCartFunnel();
        this.cartFunnelDispatcher.reset();
        return;
      case 'sync':
        if (plan.resetFirst) {
          this.cartFunnelDispatcher.leaveCartFunnel();
          this.cartFunnelDispatcher.reset();
        }
        this.addBlocksToStore(plan.stepIds);
        this.ensureDisplayableSkipWatch();
        return;
    }
  }

  private addBlocksToStore(stepIds: readonly string[]): void {
    stepIds.forEach((id, index) => {
      this.cartFunnelDispatcher.addStep({ id, index });
    });
  }

  private ensureDisplayableSkipWatch(): void {
    if (this.displayableSkipWatchArmed) {
      return;
    }
    this.displayableSkipWatchArmed = true;

    this.cartFunnelSelector.selectDisplayableStepsIds
      // Short delay so the store/effects settle before checking displayable steps.
      .pipe(delay(1), takeUntil(this._destroyerRef))
      .subscribe(displayableStepsIds => {
        if (displayableStepsIds.length === 0) {
          this.skipFunnel();
        }
      });
  }

  private skipFunnel(): void {
    if (this.cb2PreviewState.isActive()) {
      return;
    }

    const navigationHistory = this.navigationHistoryService.getHistory();
    const isLastPagePayment =
      navigationHistory[navigationHistory.length - 1]?.identifier === 'payment';
    this.navigationHistoryService.pushStateFromActivatedRouteSnapshot(
      'cart-funnel',
      this.activatedRoute.snapshot,
    );

    if (isLastPagePayment) {
      this.router.navigate([this.routerHelper.translateRoute(`/${Paths.Cart}`)]);
      return;
    }

    this.router.navigate([this.routerHelper.translateRoute(`/${Paths.Payment}`)]);
  }

  submit(): void {
    combineLatest([
      this.cartFunnelSelector.selectNextStep,
      this.cartFunnelSelector.selectIsLastStepActive,
    ])
      .pipe(
        take(1),
        map(([_next, _isLastStep]) => {
          console.log('[submit] data: ', { _next, _isLastStep });
          if (_isLastStep) {
            this.goToPaymentPage();
          } else {
            if (_next) {
              this.cartFunnelDispatcher.setActive(_next.id);

              setTimeout(() => {
                const inputs = document.querySelectorAll('input');
                if (inputs[0]) {
                  inputs[0].focus();
                }
              }, 1);
            }
          }
        }),
        tap(() => this.useFormValues()),
      )
      .subscribe();
  }

  private goToPaymentPage(): void {
    const isSharedOrderExist = this.basketSelector.basket.id;

    if (isSharedOrderExist) {
      this.router.navigate([
        this.routerHelper.translateRoute(`/${Paths.Validate}/${Paths.SharedOrder}`),
      ]);
    } else {
      this.router.navigate([this.routerHelper.translateRoute(`/${Paths.Payment}`)]);
    }
  }

  useFormValues(): void {
    this.cartFunnelSelector.selectValues
      .pipe(
        take(1),
        map(forms => {
          const orderCommentValues: string[] = [];
          let hasOnBehalfUserField = false;
          const onBehalfUser: IOnBehalfUser = {
            optin: false,
          };

          forms.forEach(form => {
            const target = form.target ?? DoodCartFunnelValueTarget.OrderComment;
            if (target === DoodCartFunnelValueTarget.OrderComment && form.value) {
              let title = null;

              if (form.displayCustomTitleInComment) {
                title = form.customTitle;
              } else if (form.displayTitle) {
                title = form.title;
              }
              let value = form.value;
              if (value === true) {
                value = 'oui';
              }

              orderCommentValues.push([title, value].filter(i => i).join(' : '));
            }
            if (target === DoodCartFunnelValueTarget.OnBehalfUserFullName) {
              onBehalfUser.full_name = form.value as string;
              hasOnBehalfUserField = true;
            }
            if (target === DoodCartFunnelValueTarget.OnBehalfUserEmail) {
              onBehalfUser.email = form.value as string;
              hasOnBehalfUserField = true;
            }
            if (target === DoodCartFunnelValueTarget.OnBehalfUserPhone) {
              onBehalfUser.phone = form.value as string;
              hasOnBehalfUserField = true;
            }
            if (target === DoodCartFunnelValueTarget.OnBehalfUserOptin) {
              onBehalfUser.optin = form.value === true;
              hasOnBehalfUserField = true;
            }
            if (target === DoodCartFunnelValueTarget.SeatCount) {
              if (form.value !== undefined && form.value !== null && form.value !== '') {
                const seatCount = typeof form.value === 'number' ? form.value : Number(form.value);
                this.cartFunnelDispatcher.setSeatCount(isNaN(seatCount) ? null : seatCount);
              } else {
                this.cartFunnelDispatcher.setSeatCount(null);
              }
            }
          });

          this.cartService.setOnBehalfUser(hasOnBehalfUserField ? onBehalfUser : undefined);
          this.cartFunnelDispatcher.updateOrderComment(orderCommentValues.join(' ; '));
        }),
        switchMap(() => this.orderDispatcher.checkCartIsValid$()),
      )
      .subscribe();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(CartFunnelPageComponent, { 'keyAliases': {} });
